import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import MobileShell from "@/components/MobileShell";
import { playAnnouncement } from "@/lib/announcer";
import {
  ClipboardList,
  CalendarDays,
  Clock,
  Activity,
  ChevronRight,
  RefreshCw,
  ChevronDown,
  UserCheck,
  Megaphone,
  Play,
  CheckCircle2,
} from "lucide-react";

const STATUS_META = {
  waiting: { label: "Waiting", cls: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
  in_progress: { label: "In Progress", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
  billing: { label: "Billing", cls: "bg-purple-500/20 text-purple-300 border-purple-500/40" },
  done: { label: "Done", cls: "bg-slate-600/30 text-slate-300 border-slate-600/50" },
};

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function formatDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function displayTime(row) {
  if (row?.reservation_time) return row.reservation_time;
  try {
    return new Date(row?.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

function patientName(row) {
  return row?.patient_snapshot?.full_name || row?.patient_name || "—";
}

function patientMeta(row) {
  const snap = row?.patient_snapshot || {};
  const bits = [snap.age ? `${snap.age} y/o` : "", snap.gender || ""].filter(Boolean);
  return bits.join(" · ");
}

function isReserved(row) {
  return row?.source === "reservation" || row?.priority === "reserved" || String(row?.id || "").startsWith("reservation-");
}

export default function DoctorMobile() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState(todayISO());
  const [queueFilter, setQueueFilter] = useState("all"); // all | waiting | in_progress | reservations

  const load = async () => {
    setBusy(true);
    try {
      const [q, r] = await Promise.all([
        api.get("/queue/doctor/today").catch(() => api.get("/queue/today")),
        api.get("/reservations", { params: { date } }).catch(() => ({ data: [] })),
      ]);
      const qData = q?.data;
      setQueue(Array.isArray(qData) ? qData : (qData?.items || []));
      setReservations(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const doctorQueue = useMemo(() => {
    const uid = user?.id;
    if (!uid) return queue;
    const assigned = queue.filter((x) => !x.assigned_doctor_id || x.assigned_doctor_id === uid);
    return assigned.length ? assigned : queue;
  }, [queue, user?.id]);

  const doctorReservations = useMemo(() => {
    const uid = user?.id;
    if (!uid) return reservations;
    const assigned = reservations.filter((x) => !x.doctor_id || x.doctor_id === uid);
    return assigned.length ? assigned : reservations;
  }, [reservations, user?.id]);

  const counts = {
    queue: doctorQueue.length,
    reservations: doctorReservations.length,
    waiting: doctorQueue.filter((x) => x.status === "waiting").length,
    inProgress: doctorQueue.filter((x) => x.status === "in_progress").length,
  };

  // Tapping a stat tile drills into that slice of the queue.
  const drillTo = (key) => {
    setQueueFilter(key);
    const target = key === "reservations" ? "reservations-section" : "queue-section";
    setTimeout(() => document.getElementById(target)?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const FILTER_LABEL = { all: "All patients", waiting: "Waiting", in_progress: "In Progress" };
  const visibleQueue = useMemo(() => {
    if (queueFilter === "waiting") return doctorQueue.filter((x) => x.status === "waiting");
    if (queueFilter === "in_progress") return doctorQueue.filter((x) => x.status === "in_progress");
    return doctorQueue;
  }, [doctorQueue, queueFilter]);

  const openPatient = (row) => {
    if (!row?.patient_id) return;
    nav(`/doctor-mobile/patient/${row.patient_id}`, {
      state: { queue_id: row.id, reservation_id: row.reservation_id, source: row.source },
    });
  };

  // Calling only announces — it plays on the Queue Display TV/speaker (which polls
  // for the latest announcement). The patient stays in the queue until Start.
  const callPatient = async (row) => {
    const name = patientName(row);
    try {
      const { data } = await api.post(`/queue/${row.id}/announce`, { call_type: "clinic" });
      // Speak on the phone too (announced twice, same as every other station).
      await playAnnouncement(data);
      toast.success(`Calling ${name} — announced everywhere`);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };

  const setQueueStatus = async (row, status) => {
    try {
      await api.put(`/queue/${row.id}/status`, { status });
      setQueue((prev) => prev.map((x) => (x.id === row.id ? { ...x, status } : x)));
      toast.success(status === "in_progress" ? "Consultation started" : status === "done" ? "Marked done" : "Updated");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };

  const arriveReservation = async (row) => {
    const rid = row?.reservation_id || row?.id;
    if (!rid) return;
    try {
      await api.post(`/reservations/${rid}/arrive`);
      toast.success("Patient checked in");
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };

  return (
    <MobileShell dark>
      <div className="px-5 pt-6 bg-slate-950">
        {/* === Heading row === */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-4xl font-extrabold leading-tight" style={{ fontFamily: "Work Sans" }}>
              eMedbay
            </div>
            <div className="text-sm font-bold text-cyan-400 tracking-[0.32em] uppercase mt-1">
              Doctor View
            </div>
          </div>
          <button
            onClick={load}
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-xl bg-white/10 border border-white/15 px-4 py-2 text-sm font-medium hover:bg-white/15 transition disabled:opacity-60"
            data-testid="doctor-refresh"
          >
            <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {/* === Date selector === */}
        <div className="mt-4 relative">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="absolute inset-0 opacity-0 cursor-pointer"
            aria-label="Pick date"
            data-testid="doctor-date"
          />
          <div className="w-full h-12 rounded-xl bg-white/5 border border-white/15 px-4 flex items-center justify-between pointer-events-none">
            <span className="text-base font-medium">{formatDate(date)}</span>
            <ChevronDown size={18} className="text-slate-400" />
          </div>
        </div>

        {/* === Big stat tiles — each drills into its own filtered list === */}
        <div className="grid grid-cols-2 gap-3 mt-5">
          <button
            className={`text-left rounded-2xl p-4 bg-gradient-to-br from-blue-600 to-cyan-400 shadow-md active:scale-[0.99] transition ${queueFilter === "all" ? "ring-2 ring-white/80" : ""}`}
            onClick={() => drillTo("all")}
            data-testid="tile-queue"
          >
            <div className="flex items-center justify-between">
              <ClipboardList size={20} className="text-white" />
              <ChevronRight size={18} className="text-white/70" />
            </div>
            <div className="text-5xl font-extrabold mt-3 leading-none">{counts.queue}</div>
            <div className="text-sm text-white/85 mt-2">Today&apos;s Queue</div>
          </button>
          <button
            className={`text-left rounded-2xl p-4 bg-gradient-to-br from-red-600 to-orange-500 shadow-md active:scale-[0.99] transition ${queueFilter === "reservations" ? "ring-2 ring-white/80" : ""}`}
            onClick={() => drillTo("reservations")}
            data-testid="tile-reservations"
          >
            <div className="flex items-center justify-between">
              <CalendarDays size={20} className="text-white" />
              <ChevronRight size={18} className="text-white/70" />
            </div>
            <div className="text-5xl font-extrabold mt-3 leading-none">{counts.reservations}</div>
            <div className="text-sm text-white/90 mt-2">Reservations</div>
          </button>

          <button
            className={`text-left rounded-2xl p-4 bg-slate-900 border active:scale-[0.99] transition ${queueFilter === "waiting" ? "border-amber-400 ring-2 ring-amber-400/40" : "border-slate-800"}`}
            onClick={() => drillTo("waiting")}
            data-testid="tile-waiting"
          >
            <div className="flex items-center justify-between">
              <Clock size={20} className="text-amber-400" />
              <ChevronRight size={18} className="text-slate-500" />
            </div>
            <div className="text-5xl font-extrabold mt-3 leading-none">{counts.waiting}</div>
            <div className="text-sm text-slate-300 mt-2">Waiting</div>
          </button>
          <button
            className={`text-left rounded-2xl p-4 bg-slate-900 border active:scale-[0.99] transition ${queueFilter === "in_progress" ? "border-emerald-400 ring-2 ring-emerald-400/40" : "border-slate-800"}`}
            onClick={() => drillTo("in_progress")}
            data-testid="tile-in-progress"
          >
            <div className="flex items-center justify-between">
              <Activity size={20} className="text-emerald-400" />
              <ChevronRight size={18} className="text-slate-500" />
            </div>
            <div className="text-5xl font-extrabold mt-3 leading-none">{counts.inProgress}</div>
            <div className="text-sm text-slate-300 mt-2">In Progress</div>
          </button>
        </div>

        {/* === Priority Queue (drilled from the tiles above) === */}
        <section className="mt-7" id="queue-section">
          <div className="flex items-end justify-between mb-3">
            <h2 className="text-xl font-extrabold" style={{ fontFamily: "Work Sans" }}>
              {queueFilter === "reservations" ? "Priority Queue" : (FILTER_LABEL[queueFilter] || "Priority Queue")}
            </h2>
            <div className="text-xs text-slate-400">{visibleQueue.length} patients</div>
          </div>

          {/* Filter chips — quick drill without scrolling back up */}
          <div className="flex gap-2 mb-3 overflow-x-auto pb-1" data-testid="queue-filters">
            {["all", "waiting", "in_progress"].map((k) => (
              <button
                key={k}
                onClick={() => setQueueFilter(k)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold border transition ${
                  queueFilter === k
                    ? "bg-white text-slate-900 border-white"
                    : "bg-slate-900 text-slate-300 border-slate-700"
                }`}
                data-testid={`queue-filter-${k}`}
              >
                {FILTER_LABEL[k]}
              </button>
            ))}
          </div>

          <div className="space-y-3" data-testid="queue-list">
            {visibleQueue.map((row) => {
              const reserved = isReserved(row);
              const status = row.status || "waiting";
              const sm = STATUS_META[status] || { label: status, cls: "bg-slate-600/30 text-slate-300 border-slate-600/50" };
              const btn = "inline-flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-semibold active:scale-[0.98] transition";
              return (
                <div
                  key={row.id}
                  className={`rounded-2xl p-4 border ${reserved ? "bg-red-500/10 border-red-400/30" : "bg-slate-900 border-slate-800"}`}
                  data-testid={`queue-row-${row.id}`}
                >
                  <div onClick={() => openPatient(row)} className="cursor-pointer active:opacity-80">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[11px] font-semibold rounded-full px-2.5 py-0.5 border ${sm.cls}`}>{sm.label}</span>
                      {reserved ? <span className="text-[11px] font-semibold rounded-full px-2.5 py-0.5 bg-red-600 text-white">Reserved</span> : null}
                      {row.queue_number ? <span className="text-[11px] font-mono text-cyan-300">#{row.queue_number}</span> : null}
                      <span className="text-[11px] text-slate-500 font-mono ml-auto">{displayTime(row)}</span>
                    </div>
                    <div className="text-xl font-extrabold mt-2 leading-tight truncate" style={{ fontFamily: "Work Sans" }}>{patientName(row)}</div>
                    <div className="text-xs text-slate-400 mt-1 truncate">{patientMeta(row) || row.reason || "No details"}</div>
                  </div>

                  {reserved && status === "waiting" ? (
                    <button onClick={() => arriveReservation(row)} className={`${btn} w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white`} data-testid={`arrive-${row.id}`}>
                      <UserCheck size={16} /> Arrived / Check In
                    </button>
                  ) : null}

                  {/* Actions: Call announces on the TV/speaker; Start begins the consult. */}
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <button onClick={() => callPatient(row)} className={`${btn} bg-blue-600 hover:bg-blue-700 text-white`} data-testid={`call-${row.id}`}>
                      <Megaphone size={16} /> Call
                    </button>
                    {status === "waiting" ? (
                      <button onClick={() => setQueueStatus(row, "in_progress")} className={`${btn} bg-emerald-600 hover:bg-emerald-700 text-white`} data-testid={`start-${row.id}`}>
                        <Play size={16} /> Start
                      </button>
                    ) : status === "in_progress" ? (
                      <button onClick={() => setQueueStatus(row, "done")} className={`${btn} bg-slate-700 hover:bg-slate-600 text-white`} data-testid={`done-${row.id}`}>
                        <CheckCircle2 size={16} /> Done
                      </button>
                    ) : (
                      <button onClick={() => openPatient(row)} className={`${btn} bg-slate-800 border border-slate-700 text-slate-200`} data-testid={`open-${row.id}`}>
                        <ChevronRight size={16} /> Open
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {visibleQueue.length === 0 ? (
              <div className="rounded-2xl border border-slate-800 bg-slate-900 p-8 text-center text-slate-400">
                {queueFilter === "waiting"
                  ? "No one is waiting right now."
                  : queueFilter === "in_progress"
                  ? "No consultations in progress."
                  : "No patients in queue yet."}
                {queueFilter !== "all" && doctorQueue.length > 0 ? (
                  <button onClick={() => setQueueFilter("all")} className="block mx-auto mt-3 text-sm font-semibold text-cyan-400" data-testid="queue-show-all">
                    Show all {doctorQueue.length} patients
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>

        {/* === Reservations (drill-down list) === */}
        <section className="mt-8 pb-12" id="reservations-section">
          <div className="flex items-end justify-between mb-3">
            <h2 className="text-xl font-extrabold" style={{ fontFamily: "Work Sans" }}>Reservations</h2>
            <div className="text-xs text-slate-400">{doctorReservations.length} today</div>
          </div>

          <div className="space-y-3" data-testid="reservations-list">
            {doctorReservations.map((r) => (
              <button
                key={r.id}
                onClick={() => r.patient_id && nav(`/doctor-mobile/patient/${r.patient_id}`)}
                className="w-full text-left rounded-2xl p-4 border bg-slate-900 border-slate-800 active:scale-[0.99]"
                data-testid={`reservation-row-${r.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-semibold rounded-full px-3 py-0.5 bg-amber-500 text-white">
                        Reserved
                      </span>
                      <span className="text-xs text-slate-400 font-mono">{r.reservation_time}</span>
                    </div>
                    <div className="text-base font-bold mt-1.5 truncate">{r.patient_name}</div>
                    <div className="text-xs text-slate-400 mt-0.5 truncate">{r.reason || ""}</div>
                  </div>
                  <ChevronRight size={18} className="text-slate-500 mt-1.5 shrink-0" />
                </div>
              </button>
            ))}

            {doctorReservations.length === 0 ? (
              <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6 text-center text-slate-400">
                No reservations for {formatDate(date)}.
              </div>
            ) : null}
          </div>
        </section>
      </div>
    </MobileShell>
  );
}
